import React, { useEffect, useState } from "react";
import moment from "moment";

import api from "services/api";
import { __ } from "services/Translator";
import RaceBanner from "components/weeklyRace/RaceBanner";
import {
  TeaserHero,
  TeaserSection,
  TeaserGrid,
  TeaserCTA,
  FeatureCard,
} from "components/auth/teaser";
import { isWeeklyRaceMockId } from "components/weeklyRace/weeklyRaceMockData";
import {
  formatPoints,
  getMaskedDisplayName,
  getMedalClass,
  getRaceEndDate,
} from "components/weeklyRace/weeklyRaceUtils";

const LB_PREVIEW = 5;

// Self-contained inline icons — no sprite / asset-file dependency.
function RIcon({ name }) {
  const paths = {
    coin: (
      <g>
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="2" />
        <path
          d="M9.6 15.4v-7l4.8 7v-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    ),
    trophy: (
      <g>
        <path d="M7 4h10v3a5 5 0 0 1-10 0V4z" />
        <rect x="10" y="12" width="4" height="4" />
        <rect x="7" y="18" width="10" height="2.5" rx="1" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          d="M7 5H4v2a3 3 0 0 0 3 3m10-5h3v2a3 3 0 0 1-3 3"
        />
      </g>
    ),
    clock: (
      <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v4.5l3 1.8" />
      </g>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      {paths[name] || paths.trophy}
    </svg>
  );
}

/**
 * TournamentsTeaser — the logged-out (stranger) view of /tournaments.
 *
 * Sells the Weekly Clover Race with a real value proposition and a working
 * Sign-Up CTA. It fetches ONLY the PUBLIC race list + leaderboard (no personal
 * endpoints) and, when a race is live, teases the leaderboard BLURRED with
 * competitor identities MASKED (getMaskedDisplayName never reads player_login).
 * No fabricated standings: the board renders only from real API rows, and prize
 * figures are shown only when the race payload actually supplies them.
 */
export default function TournamentsTeaser(props) {
  const { setModalSignUpPropsData, setModalLoginPropsData } = props;
  const cta = { setModalSignUpPropsData, setModalLoginPropsData };

  const [race, setRace] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      // Public race list — safe for a guest. api.get never rejects; a non-200
      // simply leaves the evergreen state in place.
      const res = await api.get("/race/list/active");
      if (!active || res.status !== 200) return;
      const list = Array.isArray(res.data) ? res.data : res.data?.races || [];
      const main = list[0] || null;
      if (!main) return;
      setRace(main);
      if (main.id && !isWeeklyRaceMockId(main.id)) {
        const lb = await api.get(`/race/leaderboard/${main.id}/${LB_PREVIEW}`);
        if (active && lb.status === 200 && Array.isArray(lb.data)) {
          setLeaderboard(lb.data.slice(0, LB_PREVIEW));
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const endAt = getRaceEndDate(race);
  const endsLabel = endAt
    ? `${__("Ends")} ${moment(endAt).fromNow()}`
    : __("Live now — join before it ends");
  const raceTitle = race?.lang?.title || race?.title || __("This week's race");

  const realPool =
    race &&
    (race.prize_pool_fs || race.free_spins_pool || race.total_free_spins || null);

  const legal = (
    <p className="weekly-race-teaser__legal">
      {__(
        "18+ only. Real-money wagering. Play responsibly. Terms & conditions apply. Available where permitted by law."
      )}
    </p>
  );

  return (
    <div className="weekly-race weekly-race-teaser container-xxl">
      <RaceBanner />
      <TeaserHero
        eyebrow={__("Weekly Clover Race")}
        title={__("Sign up to join the race")}
        subtitle={__(
          "Wager on qualifying slots and crash games to climb the weekly leaderboard. Every $1 wagered earns 1 point, and the top 10 racers share a free-spin prize pool."
        )}
        align="start"
        signUpLabel={__("Sign up to race")}
        {...cta}
      >
        {legal}
      </TeaserHero>

      {race ? (
        <TeaserSection
          eyebrow={
            <span className="wrt-live">
              <span className="wrt-live__dot" aria-hidden="true" />
              {__("Live now")}
            </span>
          }
          title={raceTitle}
          subtitle={endsLabel}
        >
          <div className="race-teaser-board">
            {leaderboard.length ? (
              <div className="lb-preview" aria-hidden="true">
                <div className="lb-head">
                  <span>{__("Rank")}</span>
                  <span>{__("Player")}</span>
                  <span style={{ textAlign: "right" }}>{__("Prize")}</span>
                  <span style={{ textAlign: "right" }}>{__("Points")}</span>
                </div>
                {leaderboard.map((row, index) => {
                  const rank = index + 1;
                  const rowCls =
                    "lb-row" +
                    (rank === 1
                      ? " top1"
                      : rank === 2
                      ? " top2"
                      : rank === 3
                      ? " top3"
                      : "");
                  return (
                    <div className={rowCls} key={index}>
                      <span className="rk-cell">
                        <span className={getMedalClass(rank)}>{rank}</span>
                      </span>
                      <span className="pl">
                        <span className="av">•</span>
                        <span className="nm">{getMaskedDisplayName(row)}</span>
                      </span>
                      <span className="pz">—</span>
                      <span className="mt">
                        {formatPoints(row.wagered)} {__("pts")}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="race-teaser-board__seed" aria-hidden="true">
                {__(
                  "The leaderboard fills as players race this week — sign up to claim an early lead."
                )}
              </div>
            )}

            <div className="race-teaser-board__veil">
              <span className="rtv-icon" aria-hidden="true">
                <RIcon name="trophy" />
              </span>
              <div className="rtv-title">{__("See who's winning this week")}</div>
              <p className="rtv-sub">
                {__(
                  "Sign up to reveal the full live leaderboard and take your place in the race."
                )}
              </p>
              <TeaserCTA
                align="center"
                signUpLabel={__("Sign up to race")}
                {...cta}
              />
            </div>
          </div>
        </TeaserSection>
      ) : null}

      <TeaserSection
        eyebrow={__("How it works")}
        title={__("How weekly races work")}
        subtitle={__(
          "No entry fee, no catch — just play the games you love and let every wager move you up the board."
        )}
      >
        <TeaserGrid minColWidth={240} gap={18}>
          <FeatureCard
            icon={<RIcon name="coin" />}
            title={__("Wager to earn points")}
            description={__(
              "Every $1 you wager on a qualifying slot or crash game earns you 1 point. Bonus-fund wagers don't count."
            )}
          />
          <FeatureCard
            icon={<RIcon name="trophy" />}
            title={__("Top 10 win free spins")}
            description={__(
              "When the week ends, the 10 players with the most points share the free-spin prize pool."
            )}
          />
          <FeatureCard
            icon={<RIcon name="clock" />}
            title={__("Prizes within 48 hours")}
            description={__(
              "Winnings are credited to members' accounts within 48 hours of the race closing."
            )}
          />
        </TeaserGrid>
      </TeaserSection>

      <TeaserSection
        eyebrow={__("Prizes")}
        title={
          realPool
            ? `${formatPoints(realPool)} ${__("free spins up for grabs")}`
            : __("Weekly free-spin prize pool")
        }
        subtitle={
          realPool
            ? __(
                "This week's pool is shared across the top 10 racers. Sign up and start earning points to claim your share."
              )
            : __(
                "Prizes are announced at the start of each race and shared across the top 10 finishers. Create your account so you're ready the moment the next race begins."
              )
        }
      />

      <TeaserHero
        className="weekly-race-teaser__closer"
        align="center"
        eyebrow={__("Ready to race?")}
        title={__("Create your account and get in the running")}
        subtitle={__(
          "It takes seconds to sign up. Your first qualifying spin puts you on the board."
        )}
        signUpLabel={__("Sign up to race")}
        {...cta}
      >
        {legal}
      </TeaserHero>
    </div>
  );
}
